import {useCallback, useRef, useState} from 'react';
import {useInView} from 'react-intersection-observer';
import {faCheck, faGift, faStore} from '@fortawesome/pro-regular-svg-icons';
import {
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
} from 'motion/react';
import * as motion from 'motion/react-m';
import useTranslation from 'next-translate/useTranslation';

import ValueProp from '@/components/features/home/HomeRoot/ValueProp';
import useValuePropsExperiment from '@/hooks/useValuePropsExperiment';

const SCROLL_SPEED = 0.5;

// The browser only restores a previous scroll position on reloads and back/forward loads.
const isRestoringLoad = () => {
  const navigation = performance.getEntriesByType?.('navigation')[0];
  return (
    !!navigation &&
    'type' in navigation &&
    (navigation.type === 'reload' || navigation.type === 'back_forward')
  );
};

type ValuePropsBannerProps = {
  onTreatmentShown?: () => void;
};

const ValuePropsBanner: React.FC<ValuePropsBannerProps> = ({onTreatmentShown}) => {
  const {t} = useTranslation('home');
  const {trackExposed, isTreatment} = useValuePropsExperiment();
  const [hasTrackedReveal, setHasTrackedReveal] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  const bannerRef = useRef<HTMLElement>(null);
  const hasRevealedRef = useRef(false);

  // Runs once, when the banner has started to reveal and is in the viewport.
  const handleReveal = useCallback(() => {
    if (hasRevealedRef.current) {
      return;
    }
    hasRevealedRef.current = true;
    setHasTrackedReveal(true);
    if (onTreatmentShown && isTreatment) {
      onTreatmentShown();
    }

    trackExposed();
  }, [isTreatment, onTreatmentShown, trackExposed]);

  const {scrollY} = useScroll();
  const initialScrollY = typeof window === 'undefined' ? 0 : window.scrollY;
  // Starts at the current position, so a position restored before mount never counts as scrolling.
  const lastScrollY = useRef(initialScrollY);
  // At the top on a reload or back/forward load, the restore may still arrive as the first change.
  const restorePendingRef = useRef(initialScrollY === 0);
  const lastScrollHeight = useRef<number | null>(null);
  // Total distance scrolled down so far. Only grows, so the banner reveals on scroll down from
  // anywhere on the page and never moves back under the hero.
  const revealDistance = useMotionValue(0);
  const revealProgress = useMotionValue(0);
  // min() stops the banner once it's fully in view.
  const translateY = useTransform(revealDistance, latest =>
    prefersReducedMotion
      ? latest > 0
        ? '0%'
        : '-100%'
      : `min(0px, ${latest * SCROLL_SPEED}px - 100%)`,
  );

  const isInViewRef = useRef(false);
  // Observes a sentinel covering the top of the banner's slot. It renders in both arms, so control
  // logs exposure when the banner would be in view. It's a fixed height no taller than the banner, so
  // exposure fires when any of the banner is on screen, matching in both arms.
  const {ref: inViewRef} = useInView({
    skip: hasTrackedReveal,
    // Without IntersectionObserver support, log exposure on reveal rather than throwing.
    fallbackInView: true,
    onChange: inView => {
      isInViewRef.current = inView;
      if (inView && revealDistance.get() > 0) {
        handleReveal();
      }
    },
  });

  useMotionValueEvent(scrollY, 'change', latest => {
    // iOS reports negative positions while bouncing past the top, so springing back isn't a scroll down.
    const position = Math.max(0, latest);
    const delta = position - lastScrollY.current;
    lastScrollY.current = position;
    const scrollHeight = document.documentElement.scrollHeight;
    const heightChanged =
      lastScrollHeight.current !== null && scrollHeight !== lastScrollHeight.current;
    lastScrollHeight.current = scrollHeight;
    const isLateRestore = restorePendingRef.current && isRestoringLoad();
    restorePendingRef.current = false;
    // Skip scrolls the browser made rather than the user: a restore after mount, a restore that
    // continues as the page grows, or an adjustment when content above loads. Every other scroll
    // counts, however it was made (wheel, touch, keys, scrollbar, voice control).
    if (isLateRestore || heightChanged) {
      return;
    }
    // Only scrolling down reveals the banner.
    if (delta > 0) {
      const distance = revealDistance.get() + delta;
      revealDistance.set(distance);

      const bannerHeight = bannerRef.current?.offsetHeight;
      if (bannerHeight) {
        // Not clamped at 1, so ValueProp styles keep updating as the user scrolls (CSS clamps them).
        revealProgress.set((distance * SCROLL_SPEED) / bannerHeight);
      }
    }

    if (revealDistance.get() > 0 && isInViewRef.current) {
      handleReveal();
    }
  });

  const valueProps = [
    {icon: faStore, message: t('valueProps.anyOccasion')},
    {icon: faCheck, message: t('valueProps.noHiddenFeesOrMarkups')},
    {
      icon: faGift,
      message: t('valueProps.earnPoints'),
      mobileMessage: t('valueProps.earnPointsMobile'),
    },
  ];

  return (
    <div className="relative z-10 h-0" data-testid="value-props-banner-container">
      <div
        ref={inViewRef}
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-12"
      />
      {isTreatment && (
        <motion.section
          data-testid="value-props-banner"
          ref={bannerRef}
          style={{translateY}}
          initial={false}
          animate={{opacity: prefersReducedMotion && !hasTrackedReveal ? 0 : 1}}
          transition={{duration: 0.2, ease: 'easeOut'}}
          className="absolute left-0 top-0 z-10 flex w-full justify-evenly bg-green-50 px-4 py-4 shadow-value-props-banner tablet:items-center desktop:justify-center desktop:gap-16"
        >
          {valueProps.map(({icon, message, mobileMessage}, index) => (
            <ValueProp
              key={message}
              icon={icon}
              index={index}
              message={message}
              mobileMessage={mobileMessage}
              revealProgress={revealProgress}
              total={valueProps.length}
              reduceMotion={prefersReducedMotion}
            />
          ))}
        </motion.section>
      )}
    </div>
  );
};

export default ValuePropsBanner;
